import type {
  ModelPlanDryRunResponse,
  ModelPlanPropagationRunSummary,
  ModelPlanSkuDelta,
  ModelPlanSkuDeltas,
  ModelPlanStatus,
} from '@nadoutong/contracts';
import { useT } from '../../i18n/index.ts';
import type { I18nKey, TParams } from '../../i18n/index.ts';
import { Chip } from '../../ui/index.ts';
import type { ChipTone } from '../../ui/index.ts';
import type { PlanFloorBreach, PlanFloorBreachSource } from './floorBreaches.ts';
import { fmtRate } from './floorBreaches.ts';

export { fmtRate, parsePositiveRate } from './floorBreaches.ts';

const TRIGGER_KEYS: Record<ModelPlanPropagationRunSummary['trigger'], I18nKey> = {
  items_update: 'model_plans.detail.trigger.items_update',
  rebind_restate: 'model_plans.detail.trigger.rebind_restate',
  new_model_sync: 'model_plans.detail.trigger.new_model_sync',
  manual_propagate: 'model_plans.detail.trigger.manual_propagate',
  rollback: 'model_plans.detail.trigger.rollback',
};

const STATUS_KEYS: Record<ModelPlanPropagationRunSummary['status'], I18nKey> = {
  running: 'model_plans.detail.status.running',
  done: 'model_plans.detail.status.done',
  partial_failed: 'model_plans.detail.status.partial_failed',
};

const STATUS_TONE: Record<ModelPlanPropagationRunSummary['status'], ChipTone> = {
  running: 'warn',
  done: 'ok',
  partial_failed: 'bad',
};

export function planStatusTone(status: ModelPlanStatus): ChipTone {
  return status === 'active' ? 'ok' : 'neutral';
}

export function SkuDeltasList({
  deltas,
  testId,
}: {
  readonly deltas: ModelPlanSkuDeltas;
  readonly testId: string;
}): JSX.Element {
  const t = useT();
  const rows: { readonly key: I18nKey; readonly skus: readonly ModelPlanSkuDelta[] }[] = [
    { key: 'model_plans.dry_run.added', skus: deltas.added },
    { key: 'model_plans.dry_run.removed', skus: deltas.removed },
    { key: 'model_plans.dry_run.rate_changed', skus: deltas.rate_changed },
    { key: 'model_plans.dry_run.visibility_changed', skus: deltas.visibility_changed },
  ];
  const any = rows.some((row) => row.skus.length > 0);
  if (!any) {
    return (
      <p className="m-0 text-dense text-paper-low" data-testid={`${testId}-empty`}>
        {t('model_plans.dry_run.no_sku_deltas')}
      </p>
    );
  }
  return (
    <ul className="m-0 flex list-none flex-col gap-1 p-0" data-testid={testId}>
      {rows.map((row) =>
        row.skus.length === 0 ? null : (
          <li key={row.key} className="text-dense text-paper">
            <span>{t(row.key, { count: row.skus.length })}</span>
            <span className="ml-2 inline-flex flex-wrap gap-x-2 gap-y-0.5 align-top">
              {row.skus.map((sku) => (
                // The id is the support handle, not the operator's reading
                // material: it stays reachable on hover instead of on screen.
                <span
                  key={sku.sku_id}
                  title={sku.sku_id}
                  className="font-mono text-micro text-paper-low"
                >
                  {sku.sku_label}
                </span>
              ))}
            </span>
          </li>
        ),
      )}
    </ul>
  );
}

type Translate = (key: I18nKey, params?: TParams) => string;

function sourceLabel(source: PlanFloorBreachSource, t: Translate): string {
  return source === 'group_default'
    ? t('model_plans.floor.source.group_default')
    : t('model_plans.floor.source.sku_override');
}

export function floorBreachRowText(breach: PlanFloorBreach, t: Translate): string {
  if (breach.floor_multiplier === null) {
    // A server breach row with no floor value: the SKU has no configured
    // sales floor, so the backend fell back to its baseline (procurement)
    // floor — whose number is never projected to this surface. Saying only
    // "unconfigured" reads as a data remark, not a rejection; say why the
    // row is here and what unblocks it.
    return breach.resolved_rate === null
      ? t('model_plans.floor.row_unknown_floor', { sku: breach.sku_name })
      : t('model_plans.floor.row_unknown_floor_rate', {
          sku: breach.sku_name,
          rate: fmtRate(breach.resolved_rate),
        });
  }
  if (breach.resolved_rate === null) {
    return t('model_plans.floor.row_no_rate', {
      sku: breach.sku_name,
      floor: fmtRate(breach.floor_multiplier),
    });
  }
  if (breach.source === null) {
    return t('model_plans.floor.row_no_source', {
      sku: breach.sku_name,
      rate: fmtRate(breach.resolved_rate),
      floor: fmtRate(breach.floor_multiplier),
    });
  }
  return t('model_plans.floor.row', {
    sku: breach.sku_name,
    rate: fmtRate(breach.resolved_rate),
    floor: fmtRate(breach.floor_multiplier),
    source: sourceLabel(breach.source, t),
  });
}

export function floorHintText(breach: PlanFloorBreach, t: Translate): string {
  if (breach.resolved_rate === null || breach.floor_multiplier === null || breach.source === null) {
    return floorBreachRowText(breach, t);
  }
  return t('model_plans.floor.hint', {
    rate: fmtRate(breach.resolved_rate),
    floor: fmtRate(breach.floor_multiplier),
    source: sourceLabel(breach.source, t),
  });
}

export function FloorBreachesList({
  breaches,
  testId,
}: {
  readonly breaches: readonly PlanFloorBreach[];
  readonly testId: string;
}): JSX.Element | null {
  const t = useT();
  if (breaches.length === 0) return null;
  return (
    <div
      role="alert"
      data-testid={testId}
      className="flex flex-col gap-1 rounded-lg border border-bad-line bg-bad-dim/50 px-3 py-2 text-label text-bad-bright"
    >
      <span>{t('model_plans.floor.breaches_title')}</span>
      <ul className="m-0 flex list-none flex-col gap-0.5 p-0 font-mono">
        {breaches.map((breach) => (
          <li key={breach.sku_id} data-testid={`${testId}-row-${breach.sku_id}`}>
            {floorBreachRowText(breach, t)}
          </li>
        ))}
      </ul>
      {breaches.some((breach) => breach.floor_multiplier === null) ? (
        <span className="text-warn" data-testid={`${testId}-unconfigured-hint`}>
          {t('model_plans.floor.unconfigured_hint')}
        </span>
      ) : null}
    </div>
  );
}

export function BlastRadiusPreview({
  preview,
  floorBreaches,
  testId,
}: {
  readonly preview: ModelPlanDryRunResponse;
  readonly floorBreaches: readonly PlanFloorBreach[];
  readonly testId: string;
}): JSX.Element {
  const t = useT();
  return (
    <div className="flex flex-col gap-3" data-testid={testId}>
      <p className="m-0 text-dense text-paper-mid">{t('model_plans.dry_run.body')}</p>
      <div className="flex flex-wrap gap-2 text-dense text-paper">
        <span>{t('model_plans.dry_run.bound_codes', { count: preview.bound_active_code_count })}</span>
        <span aria-hidden="true">·</span>
        <span>{t('model_plans.dry_run.assigned_orgs', { count: preview.assigned_org_count })}</span>
        <span aria-hidden="true">·</span>
        <span>
          {t('model_plans.dry_run.skipped_manual', { count: preview.estimated_skipped_manual })}
        </span>
      </div>
      <SkuDeltasList deltas={preview.sku_deltas} testId={`${testId}-deltas`} />
      <FloorBreachesList breaches={floorBreaches} testId={`${testId}-breaches`} />
    </div>
  );
}

export function PropagationRunSummaryView({
  run,
  testId,
}: {
  readonly run: ModelPlanPropagationRunSummary;
  readonly testId: string;
}): JSX.Element {
  const t = useT();
  const skipped = Object.entries(run.stats.skipped);
  return (
    <div className="flex flex-col gap-2" data-testid={testId}>
      <div className="flex flex-wrap items-center gap-2">
        <Chip tone={STATUS_TONE[run.status]} testId={`${testId}-status`}>
          {t(STATUS_KEYS[run.status])}
        </Chip>
        <span className="text-dense text-paper">{t(TRIGGER_KEYS[run.trigger])}</span>
      </div>
      <p className="m-0 text-dense text-paper-mid">
        {t('model_plans.detail.stats', {
          orgs: run.stats.orgs_total,
          updated: run.stats.orgs_updated,
          upserted: run.stats.rows_upserted,
          deleted: run.stats.rows_deleted,
        })}
      </p>
      {skipped.length === 0 ? null : (
        <ul className="m-0 flex list-none flex-col gap-0.5 p-0 text-micro text-paper-low">
          {skipped.map(([reason, count]) => (
            <li key={reason}>{t('model_plans.detail.skipped', { reason, count })}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
